import React, { useRef } from 'react';
import CrudListPage from './CrudListPage';
import SectionCard from '../ui/SectionCard';
import Button from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import { PlusIcon, TagIcon, UserGroupIcon } from '@heroicons/react/24/outline';

/**
 * CustomerTypesPage — two-section Settings surface.
 *
 * Sections:
 *   1. Customer Types    — the taxonomy operators use to categorize customers
 *                          (Residential, Commercial, etc.). custom:true — tenants
 *                          may add rows via this page.
 *   2. Contact Roles     — the role a contact plays on a customer record
 *                          (Primary, Billing, Foreman, etc.). custom:true.
 *
 * PR-B2b-part-1 (2026-07-21): refactored from single-section CrudListPage into
 * a two-section (SectionCard + bare CrudListPage) layout so Contact Roles has a
 * management surface. Migrated CustomerFormPanel.js away from the hardcoded
 * CONTACT_ROLE_OPTIONS in the same PR.
 *
 * Both sections seed on first visit from `constants/vocabSeeds.js` — the
 * single canonical `definition_home` for these sets. See ADR block in
 * vocabSeeds.js.
 *
 * Iter18 R2 §2.10 + Round 4 (D) predecessor: the single-section version
 * intentionally removed SectionCard because it created a "duplicate title +
 * description" visual (PageHeader + SectionCard both rendered the title). This
 * refactor uses a SINGLE PageHeader at the top of the page + two SectionCards
 * WITHOUT their own titles overlapping the page title. Section titles are the
 * sub-taxonomy names ("Customer Types", "Contact Roles"), which are distinct
 * from the page title ("Customer Types & Contact Roles").
 */

// IDI-293: the seed-on-first-visit hook that lived here WROTE the default
// customer types / contact roles whenever a read came back empty, which
// included a failed read and a migrated dealer whose list hadn't migrated
// yet. Seeding is the backend's job (net-new tenants only); this page reads.

// ─── page ────────────────────────────────────────────────────────────────────

export default function CustomerTypesPage() {
  // Imperative refs so each SectionCard's "+ Add" action can trigger the
  // corresponding CrudListPage's add flow. See CrudListPage `bare` mode + the
  // exposed `openAdd()` on the forwardRef.
  const customerTypesRef = useRef(null);
  const contactRolesRef = useRef(null);

  const addButton = (ref, label) => (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => ref.current?.openAdd()}
    >
      <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
      {label}
    </Button>
  );

  const commonFields = [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g., Residential, Commercial' },
    { key: 'description', label: 'Description', type: 'text', required: false, placeholder: 'Optional description' },
  ];

  // PT-30 (2026-07-26): customer types carry a `default_margin_pct` — Lindsey
  // level 3 in the margin resolution hierarchy. Accepted as 0–100 (percentage);
  // consumers (QuoteBuilderPage.customerTypeMargin) coerce to a 0..1 decimal at
  // read time. Blank = "cascade to the next Lindsey level" (customerTypeMargin
  // → quoteMargin). See docs/pt-30/PRECEDENCE.md.
  const customerTypeFields = [
    { key: 'name',               label: 'Name',              type: 'text',   required: true,  placeholder: 'e.g., Residential, Commercial' },
    { key: 'description',        label: 'Description',       type: 'text',   required: false, placeholder: 'Optional description' },
    { key: 'default_margin_pct', label: 'Default margin %',  type: 'number', required: false, placeholder: 'e.g., 30 (leave blank to inherit quote margin)' },
  ];

  return (
    <div>
      <PageHeader
        title="Customer Types & Contact Roles"
        description="Manage the categories used to classify customers and the roles their contacts play on the customer record."
      />

      <div className="space-y-4">
        <SectionCard
          title="Customer Types"
          description="How your customers are categorized (e.g., Residential, Commercial). Applied to the customer record and used to drive T&C templates + reporting groupings."
          icon={TagIcon}
          actions={addButton(customerTypesRef, 'Add')}
        >
          <CrudListPage
            ref={customerTypesRef}
            bare
            listType="customer_types"
            fields={customerTypeFields}
            emptyMessage="No customer types defined. Click Add customer type to create the first."
          />
        </SectionCard>

        <SectionCard
          title="Contact Roles"
          description="The role a contact plays on the customer record (e.g., Primary, Billing, Foreman). Applied per-contact in the customer form."
          icon={UserGroupIcon}
          actions={addButton(contactRolesRef, 'Add')}
        >
          <CrudListPage
            ref={contactRolesRef}
            bare
            listType="contact_roles"
            fields={commonFields.map((f, i) =>
              i === 0 ? { ...f, placeholder: 'e.g., Primary, Billing, Foreman' } : f
            )}
            emptyMessage="No contact roles defined. Click Add contact role to create the first."
          />
        </SectionCard>
      </div>
    </div>
  );
}
